import React, { useMemo, useState } from 'react';

import { BroadcastVsDigitalFilter } from '@trqk/components';
import { constants, getTextDialFilterData, removeNulls } from '@trqk/utils';

import { PerformanceContributionsTable } from '.';

import { defaultFilterOrder } from '../../constants';

const {
    broadcastFilterPredicate,
    broadcastOption,
    broadcastVsDigitalOptions,
    digitalFilterPredicate,
} = constants;

interface PerformanceDataProps {
    /**
     * A label including the PRO and time period
     */
    periodLabel: string;
    /**
     * A timestamp indicating that the PerformanceContributionsTable data should be refreshed
     */
    refreshTable: number;
    /**
     * The merged ProTrack data containing the performance contributions to be rendered in the
     * PerformanceContributionsTable
     */
    trackData: ProTrack;
    /**
     * Whether to filter and slice data
     */
    truncateChartData: boolean;
}

/**
 * Renders a table of performance data with a filter to view Digital vs. Broadcast data (which is
 * calculated from the music_user_genre_name value).
 * @param {PerformanceDataProps}
 */
export const PerformanceData = ({
    periodLabel,
    refreshTable,
    trackData,
    truncateChartData,
}: PerformanceDataProps): JSX.Element => {
    const performanceContributions = removeNulls(
        trackData.performance_contributions,
    );

    const dataConfig = useMemo(
        () =>
            getTextDialFilterData<ProPerformanceContribution>({
                data: performanceContributions,
                filterConfigs: {
                    broadcast: {
                        filterPredicate: broadcastFilterPredicate,
                        sort: {
                            key: 'earnings',
                            order: 'desc',
                        },
                    },
                    digital: {
                        filterPredicate: digitalFilterPredicate,
                        sort: {
                            key: 'earnings',
                            order: 'desc',
                        },
                    },
                },
                defaultFilterOrder,
                truncateChartData,
            }),
        [performanceContributions, truncateChartData],
    );

    const initialFilterOption =
        broadcastVsDigitalOptions.get(
            (dataConfig?.defaultFilter ?? 'broadcast') as BroadcastOrDigital,
        ) ?? broadcastOption;

    const [filterOption, setFilterOption] = useState(initialFilterOption);
    const { data } = dataConfig.filteredData[filterOption.value];

    return (
        <>
            <div className="flex justify-between items-start">
                <div className="text-tertiary-400 text-sm">{periodLabel}</div>
                <div>
                    <BroadcastVsDigitalFilter
                        filterOption={filterOption}
                        setFilterOption={setFilterOption}
                    />
                </div>
            </div>
            <div className="mb-8 text-lg text-tertiary-500 font-semibold">
                Performance Data
            </div>
            <PerformanceContributionsTable
                performanceContributions={data}
                refreshTable={refreshTable}
                filterValue={filterOption.value}
            />
        </>
    );
};
